import {
  createDeleteItemFormData,
  createAddItemFormData,
  createAddToCartListFormData,
  listActionStandard,
  openOptionalModal,
} from './helper';
import { messageBoxSuccess, messageBoxError } from '../components/message-box';

class ProductEditor {
  public selectedElements: any = [];

  public productTeasers: NodeListOf<HTMLElement>;

  public productTeaserTrigger: NodeListOf<HTMLElement>;

  public productEditor: HTMLElement;

  public productEditorAmount: HTMLElement;

  public productEditorSelectAll: HTMLElement;

  public actionButtons: NodeListOf<HTMLButtonElement>;

  public confirmPopup: HTMLElement;

  public select: HTMLSelectElement;

  public selectOptions: NodeListOf<HTMLElement>;

  public addToCartButtons: NodeListOf<HTMLElement>;

  public wishlistContent: any;

  public cartIconHeader: HTMLElement | null;

  public cartIconHeaderInner: SVGTSpanElement | null | undefined;

  public lastScroll: number = 0;

  constructor(wishlistContent: any) {
    this.wishlistContent = wishlistContent;
  }

  updateEditor() {
    this.productEditorAmount.innerHTML = this.selectedElements.length;
  }

  handleToggleSelection(e: Event) {
    const productTeaser = (e.currentTarget as HTMLElement).closest('.m-wishlist-product-teaser') as HTMLElement;

    if (productTeaser.classList.contains('is-selected')) {
      productTeaser.classList.remove('is-selected');
      this.selectedElements = this.selectedElements.filter((element: any) => element.elementId !== productTeaser.dataset.elementId);
    } else {
      productTeaser.classList.add('is-selected');
      this.selectedElements.push({
        title: productTeaser.dataset.itemTitle,
        wishlistId: productTeaser.dataset.wishlistId,
        elementId: productTeaser.dataset.elementId,
        listId: productTeaser.dataset.listId,
        quantity: productTeaser.dataset.quantity,
        hidePrice: productTeaser.dataset.hidePrice,
        purchasable: productTeaser.dataset.purchasable,
        sku: productTeaser.dataset.sku,
        withAccessories: productTeaser.dataset.withAccessories,
      });
    }

    if (!this.selectedElements.length) {
      this.productEditor.classList.remove('is-active');
    } else if (!this.productEditor.classList.contains('is-active')) {
      this.productEditor.classList.add('is-active');
      setTimeout(() => {
        this.productEditor.classList.add('visible');
      }, 100);
    }

    this.updateEditor();
  }

  handleToggleSelectAll() {
    if (this.selectedElements.length === this.productTeasers.length) {
      this.productTeasers.forEach((teaser) => {
        teaser.classList.remove('is-selected');
      });

      this.productEditor.classList.remove('is-active');
      this.selectedElements = [];
    } else {
      this.selectedElements = [];

      this.productTeasers.forEach((teaser) => {
        teaser.classList.add('is-selected');
        this.selectedElements.push({
          title: teaser.dataset.itemTitle,
          wishlistId: teaser.dataset.wishlistId,
          elementId: teaser.dataset.elementId,
          listId: teaser.dataset.listId,
          purchasable: teaser.dataset.purchasable,
          quantity: teaser.dataset.quantity,
          hidePrice: teaser.dataset.hidePrice,
          sku: teaser.dataset.sku,
          withAccessories: teaser.dataset.withAccessories,
        });
      });
    }

    this.updateEditor();
  }

  async handleActionButton(e: Event) {
    const target = e.currentTarget as HTMLElement;

    switch(target.dataset.action) {
      case 'clone':
        let items: any = [];

        const requests = [];

        try {
          for (const element of this.selectedElements) {
            items.push(element.title);

            const newItemFormData = createAddItemFormData(element.elementId, element.listId);
            newItemFormData.append('fields[plaintext_title_1]', element.quantity);
            newItemFormData.append('fields[plaintext_title_2]', element.hidePrice);

            requests.push(listActionStandard(newItemFormData));
          }

          await Promise.all(requests);

          messageBoxSuccess({
            contentDe: `Produkt(e) „${items.join(', ')}“ wurde(n) erfolgreich dupliziert.`,
            contentEn: `Successfully cloned product(s) „${items.join(', ')}“.`,
          });

          setTimeout(() => {window.location.reload()}, 200);
        } catch (e) {
          console.error(e);

          messageBoxError({
            contentDe: 'Es ist ein Fehler aufgetreten. Produkt(e) konnte nicht dupliziert werden.',
            contentEn: 'An error appeared. Could not clone product(s).',
          });
        }
        break;
      case 'delete':
        this.confirmPopup.classList.add('m-popup--is-active');

        const confirmDeleteButton = this.confirmPopup.querySelector('.a-button:last-child') as HTMLElement;

        confirmDeleteButton.addEventListener('click', async (e: Event) => {
          e.preventDefault();

          const requests = [];

          try {
            for (const element of this.selectedElements) {
              requests.push(listActionStandard(createDeleteItemFormData(element.elementId, element.listId)));
            }

            await Promise.all(requests);

            messageBoxSuccess({
              contentDe: 'Produkt(e) wurde(n) erfolgreich gelöscht.',
              contentEn: 'Successfully deleted product(s).',
            });

            setTimeout(() => {window.location.reload()}, 200);
          } catch (e) {
            console.error(e);

            messageBoxError({
              contentDe: 'Es ist ein Fehler aufgetreten. Produkt(e) konnte(n) nicht gelöscht werden.',
              contentEn: 'An error appeared. Could not delete product(s).',
            });
          }
        });
        break;
      default:
    }
  }

  async handleMoveProduct() {
    const listToMoveTo = this.select.selectedOptions[0] as HTMLElement;

    const requests = [];

    try {
      for (const element of this.selectedElements) {
        requests.push(listActionStandard(createDeleteItemFormData(element.elementId, element.listId)));

        const newItemFormData = createAddItemFormData(element.elementId, listToMoveTo.dataset.listId as string);
        newItemFormData.append('fields[plaintext_title_1]', element.quantity);
        newItemFormData.append('fields[plaintext_title_2]', element.hidePrice);

        requests.push(listActionStandard(newItemFormData));
      }

      await Promise.all(requests);

      messageBoxSuccess({
        contentDe: 'Produkt(e) wurde(n) erfolgreich verschoben.',
        contentEn: 'Successfully moved product(s).',
      });

      setTimeout(() => {window.location.reload()}, 200);
    } catch (e) {
      console.error(e);

      messageBoxError({
        contentDe: 'Es ist ein Fehler aufgetreten. Produkt(e) konnte(n) nicht verschoben werden.',
        contentEn: 'An error appeared. Could not move product(s).',
      });
    }
  }

  async addProductsToCart(e: Event) {
    e.preventDefault();

    document.body.classList.add('loading-bar');

    try {
      let allProductsAmount = 0;
      let addedToCartProductsAmount = 0;
      let configurationInListGlobal = false;
      let configurationInList = false;
      let unavailableProductInList = false;
      let unavailableProductInListGlobal = false;
      let accessoriesProducts: { id: any; quantity: any; }[] = [];

      const requests = [];

      for (const element of this.selectedElements) {
        allProductsAmount += 1;

        if (!element.hidePrice) {
          if(element.purchasable) {

            const addItemToCartFormdata = createAddToCartListFormData(element.listId);

            addItemToCartFormdata.append(`purchasables[${element.wishlistId}]`, element.wishlistId);
            addItemToCartFormdata.append(`purchasables[${element.wishlistId}][qty]`, element.quantity);

            if (element['sku'] != null && element['sku'] != "" && element['withAccessories'] != null && element['withAccessories'] != "") {
              accessoriesProducts.push({
                'id' : element['withAccessories'],
                'quantity' : element.quantity
              });
            }

            addedToCartProductsAmount += 1;
            requests.push(listActionStandard(addItemToCartFormdata));

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null }); // reset data layer items
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                // currency: currency, // currency
                // value: selectedProduct.price.price, // price including discount
                content_type: 'product', // static
                content_ids: [element.sku], // SKU
                items: [
                  {
                    item_id: element.sku, // SKU
                    item_name: element.title, // product name
                    // item_category: selectedProduct.product_type_label, // product category (category ID would be fine as well)
                    // configuration_id: elementId, // configuration ID, if applicable
                    // price: selectedProduct.price.price, // original price
                    // discount: null, // discount, if applicable
                    quantity: element.quantity // static
                  }
                ]
              }
            });

          } else {
            unavailableProductInList = true;
            unavailableProductInListGlobal = true;
          }
        } else {
          configurationInList = true;
          configurationInListGlobal = true;
        }
      }

      await Promise.all(requests);

      if (accessoriesProducts.length > 0) {
        const requiredAccessoriesEvent = new CustomEvent('formpopup:open', {
          detail: {
            form: 'required-accessories',
            type: 'form',
            size: 'large--py-0',
            target: 'form-popup',
            products: JSON.stringify(accessoriesProducts),
            hideMessageBox: true,
          },
        } as any);

        document.dispatchEvent(requiredAccessoriesEvent);

        const handleAfterModal = () => {
          if (configurationInListGlobal || unavailableProductInListGlobal) {
            messageBoxSuccess({
              contentDe: `${addedToCartProductsAmount} von ${allProductsAmount} Produkten wurden erfolgreich zum Warenkorb hinzugefügt.`,
              contentEn: `${addedToCartProductsAmount} of ${allProductsAmount} products successfully added to cart.`,
            });
          } else {
            messageBoxSuccess({
              contentDe: 'Produkt(e) wurde(n) erfolgreich zum Warenkorb hinzugefügt.',
              contentEn: 'Successfully added product(s) to cart.',
            });
          }

          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { showCart: true },
          } as any));

          setTimeout(() => {
            openOptionalModal(configurationInListGlobal, unavailableProductInListGlobal);
          }, 4500);

          window.removeEventListener('required-accessories:close', handleAfterModal);
        };

        window.addEventListener('required-accessories:close', handleAfterModal);
      } else {
        messageBoxSuccess({
          contentDe: 'Produkt(e) wurde(n) erfolgreich zum Warenkorb hinzugefügt.',
          contentEn: 'Successfully added product(s) to cart.',
        });

        setTimeout(() => {
          openOptionalModal(configurationInListGlobal, unavailableProductInListGlobal);
        }, 4500);
      }
      const apiBaseUrl = document.body.dataset.apiBaseUrl;
      const cartResponse = await fetch(`${apiBaseUrl}api/v1/cart`, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
        },
      });
      const cart = await cartResponse.json();

      this.cartIconHeader = document.querySelector('.l-header__actions-button-cart .js-l-header__cart-icon');
      this.cartIconHeaderInner = this.cartIconHeader?.querySelector('tspan');

      if (this.cartIconHeader) {
        this.cartIconHeader.style.display = 'block';
      }

      if (this.cartIconHeaderInner) {
        let sum = 0;
        let alreadyCounted: any[] = [];
        cart.lineItems.forEach((lineItem: any) => {
          if (!lineItem.options?._configuratorId || lineItem.options?._configuratorMainItem === 'yes') {
            sum += lineItem.qty;
          }
          alreadyCounted.push(lineItem.options?._configuratorId);
        });
        this.cartIconHeaderInner.innerHTML = String(sum);
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { showCart: accessoriesProducts.length > 0 ? false : true },
      } as any));

      document.body.classList.remove('loading-bar');
    } catch (e) {
      console.error(e);

      document.body.classList.remove('loading-bar');

      messageBoxError({
        contentDe: 'Es ist ein Fehler aufgetreten. Produkt(e) konnte(n) nicht zum Warenkorb hinzugefügt werden.',
        contentEn: 'An error appeared. Could not add product(s) to cart.',
      });
    }
  }

  bindings() {
    if (this.productTeaserTrigger) {
      this.productTeaserTrigger.forEach((trigger) => {
        trigger.addEventListener('click', this.handleToggleSelection.bind(this));
      });
    }

    if (this.productEditorSelectAll) {
      this.productEditorSelectAll.addEventListener('click', this.handleToggleSelectAll.bind(this));
    }

    if (this.actionButtons) {
      this.actionButtons.forEach((button) => {
        button.addEventListener('click', this.handleActionButton.bind(this));
      });
    }

    if (this.confirmPopup) {
      this.confirmPopup.querySelector('.a-button:first-child')?.addEventListener('click', () => {
        this.confirmPopup.classList.remove('m-popup--is-active');
      });
    }

    if (this.select) {
      this.select.addEventListener('change', this.handleMoveProduct.bind(this));
    }

    if (this.addToCartButtons) {
      this.addToCartButtons.forEach((button) => {
        button.addEventListener('click', this.addProductsToCart.bind(this));
      });
    }

    window.addEventListener('scroll', () => {
      const newScroll = window.scrollY;

      if (newScroll > this.lastScroll && window.scrollY > 500) {
        this.productEditor?.classList.remove('visible');
      } else {
        this.productEditor?.classList.add('visible');
      }

      this.lastScroll = newScroll;
    });
  }

  cache() {
    this.productTeasers = document.querySelectorAll('.m-wishlist-product-teaser');
    this.productTeaserTrigger = document.querySelectorAll('.m-wishlist-product-teaser__selection');
    this.productEditor = document.querySelector('.m-wishlist-product-editor') as HTMLElement;
    this.productEditorAmount = this.productEditor?.querySelector('.m-wishlist-product-editor__products-amount') as HTMLElement;
    this.productEditorSelectAll = this.productEditor?.querySelector('.m-wishlist-product-editor__select-button') as HTMLElement;
    this.actionButtons = this.productEditor?.querySelectorAll('.a-wishlist-button');
    this.confirmPopup = document.querySelector('.m-wishlist-popup.m-confirm-popup') as HTMLElement;
    this.select = this.productEditor?.querySelector('.a-wishlist-dropdown select') as HTMLSelectElement;
    this.selectOptions = this.productEditor?.querySelectorAll('.a-wishlist-dropdown select option:not(.group)') as NodeListOf<HTMLElement>;
    this.addToCartButtons = this.productEditor?.querySelectorAll('.js-add-to-cart');
    this.cartIconHeader = document.querySelector('.l-header__actions-button-cart .js-l-header__cart-icon');
    this.cartIconHeaderInner = this.cartIconHeader?.querySelector('tspan');
  }

  init() {
    this.cache();
    this.bindings();
  }
}

export default ProductEditor;
